import { Edit } from "@refinedev/mui";
import {
  Box,
  TextField,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import {
  IResourceComponentsProps,
  useTranslate,
  useApiUrl,
} from "@refinedev/core";
import { useParams } from "react-router-dom";
import { Controller } from "react-hook-form";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useEffect, useState } from "react";
import { IEvent } from "./EventList";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import axiosInstance from "../../api/axios-config";
import { parseISO } from "date-fns";
import { UserSearch } from "../../components/search/search";
import { UserSearchResult } from "../../interfaces/search";
import AddressAutocomplete, { GeoapifyResult } from "../../components/forms/AddressAutocomplete";
import LocationMap from "../../components/forms/LocationMap";
import axios from "axios";

export const EventEdit: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const apiUrl = useApiUrl();
  const params = useParams();
  const baseApiUrl = apiUrl;

  // Security list state management
  const [securityInput, setSecurityInput] = useState("");
  const [securityList, setSecurityList] = useState<
    Array<{
      value: string;
      type: "email" | "phone";
      exists: boolean | null;
      name?: string;
      email?: string;
      phone?: string;
      id?: number;
    }>
  >([]);
  const [inputError, setInputError] = useState<string | null>(null);

  // Search results state
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State to track map coordinates
  const [mapCoordinates, setMapCoordinates] = useState({
    latitude: 0,
    longitude: 0,
  });

  const {
    refineCore: { formLoading, queryResult },
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm<IEvent>({
    refineCoreProps: {
      resource: "safe-events",
      action: "edit",
      id: params?.id,
      redirect: "list",
      onMutationError: (error) => {
        console.error("Error updating event:", error);
      },
    },
  });

  // Watch fields for changes from user input
  React.useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'latitude' || name === 'longitude') {
        const lat = value.latitude;
        const lng = value.longitude;
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          setMapCoordinates({
            latitude: lat,
            longitude: lng
          });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const EventData = queryResult?.data?.data;

  // Log the data received from the API to debug
  useEffect(() => {
    if (EventData) {
      // Set initial map coordinates from event data
      if (EventData.latitude && EventData.longitude) {
        setMapCoordinates({
          latitude: EventData.latitude,
          longitude: EventData.longitude
        });
      }

      // Ensure dates are proper Date objects before setting form values
      if (EventData.startDate) {
        try {
          const startDate =
            typeof EventData.startDate === "string"
              ? parseISO(EventData.startDate)
              : new Date(EventData.startDate);
          setValue("startDate", startDate);
        } catch (error) {
          console.error("Error parsing startDate:", error);
          setValue("startDate", new Date());
        }
      }

      if (EventData.endDate) {
        try {
          const endDate =
            typeof EventData.endDate === "string"
              ? parseISO(EventData.endDate)
              : new Date(EventData.endDate);
          setValue("endDate", endDate);
        } catch (error) {
          console.error("Error parsing endDate:", error);
          setValue("endDate", new Date(Date.now() + 24 * 60 * 60 * 1000));
        }
      }

      // If the event has existing security contacts, add them to the state
      if (
        EventData.securityContacts &&
        Array.isArray(EventData.securityContacts)
      ) {
        const securityItems = EventData.securityContacts
          .map((contact) => {
            const type = contact.email ? "email" : "phone";
            const value = contact.email || contact.phone;
            return {
              type: type as "email" | "phone",
              value,
              exists: true, // These users definitely exist in the database
              id: contact.id, // Preserve the user ID
              name: contact.fullname || contact.email || contact.phone,
              email: contact.email,
              phone: contact.phone
            };
          })
          .filter((item) => item.value); // Filter out any empty values
        setSecurityList(securityItems);
      }
    }
  }, [EventData, setValue]);

  // Validation functions for email and phone
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone: string) => {
    // Basic phone validation - can be enhanced as needed
    return /^\+?[0-9]{10,15}$/.test(phone);
  };

  // Search for users by email or phone
  const handleSearchUsers = async () => {
    if (!securityInput.trim()) {
      setInputError("Please enter an email or phone number to search");
      return;
    }
    setInputError(null);

    try {
      // Check if the input is an email or phone
      let type = isValidEmail(securityInput) ? "email" : "phone";

      // Try first with POST method (like in EventCreate)
      try {
        const response = await axiosInstance.post(
          `${baseApiUrl}/users/search`,
          { search: securityInput },
        );

        const userData = Array.isArray(response.data)
          ? response.data[0]
          : response.data;

        // Process profile picture URL if it exists
        if (userData) {
          if (
            userData.profilePicture &&
            !userData.profilePicture.startsWith("http")
          ) {
            userData.profilePicture = `${baseApiUrl}/uploads/${userData.profilePicture}`;
          }
          setSearchResults([userData]);
          return;
        }
      } catch (error) {
        // Silently fall back to GET method
      }

      // Try with GET method if POST fails
      const response = await axiosInstance.get(`${baseApiUrl}/users/check`, {
        params: { [type]: securityInput },
      });

      // If user exists, fetch their details
      if (response.data.exists) {
        // Use findByEmailOrPhone to get full user details
        const userResponse = await axiosInstance.get(`${baseApiUrl}/users/search`, {
          params: { contact: securityInput },
        });

        if (userResponse.data) {
          const userData = userResponse.data;
          // Process profile picture URL if it exists
          if (
            userData.profilePicture &&
            !userData.profilePicture.startsWith("http")
          ) {
            userData.profilePicture = `${baseApiUrl}/uploads/${userData.profilePicture}`;
          }
          setSearchResults([userData]);
        } else {
          // Create a minimal user object since we can't fetch the full details
          const minimalUser = {
            id: Date.now(), // Placeholder ID
            [type]: securityInput,
            email: type === "email" ? securityInput : "",
            phone: type === "phone" ? securityInput : "",
            fullname: "User Found",
          };
          setSearchResults([minimalUser]);
        }
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching for users:", error);
      setInputError(
        `Error searching for users: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      setSearchResults([]);
    }
  };

  // Check if user exists in database
  const checkUserExists = async (value: string, type: "email" | "phone") => {
    try {
      const endpoint = `${baseApiUrl}/users/check`;

      const response = await axiosInstance.get(endpoint, {
        params: { [type]: value },
      });

      return !!response.data.exists;
    } catch (error) {
      console.error("Error checking user existence:", error);
      return false;
    }
  };

  // Handle adding user from search results to security list
  const handleAddUserFromSearch = async (user: UserSearchResult) => {
    const value = user.email || user.phone || "";
    const type: "email" | "phone" = user.email ? "email" : "phone";

    if (!value) {
      setInputError("User has no email or phone number");
      return;
    }

    // Check if already in the list
    if (securityList.some((item) => item.value === value)) {
      setInputError("This entry is already in the list");
      return;
    }

    // Save user ID as well as other details with proper typing
    const newSecurityItem = {
      value,
      type,
      exists: true as boolean, // Explicitly type as boolean, not null
      name: user.fullname,
      email: user.email,
      phone: user.phone,
      id: user.id
    };

    // Add to list with confirmed existence and all user details
    setSecurityList((prev) => [...prev, newSecurityItem]);

    // Clear search results after adding
    setSearchResults([]);
  };

  // Handle adding new security item manually
  const handleAddSecurity = async () => {
    if (!securityInput.trim()) {
      setInputError("Please enter an email or phone number");
      return;
    }

    let type: "email" | "phone";
    if (isValidEmail(securityInput)) {
      type = "email";
    } else if (isValidPhone(securityInput)) {
      type = "phone";
    } else {
      setInputError("Please enter a valid email or phone number");
      return;
    }
    setInputError(null);

    // Check if already in the list
    if (securityList.some((item) => item.value === securityInput)) {
      setInputError("This entry is already in the list");
      return;
    }

    // Add to list with "checking" status
    setSecurityList((prev) => [
      ...prev,
      { value: securityInput, type, exists: null },
    ]);

    // Check existence
    const exists = await checkUserExists(securityInput, type);

    // Update item with existence status
    setSecurityList((prev) =>
      prev.map((item) =>
        item.value === securityInput ? { ...item, exists } : item
      )
    );

    // Clear input field
    setSecurityInput("");
  };

  // Remove security item from list
  const handleRemoveSecurity = (value: string) => {
    setSecurityList((prev) => prev.filter((item) => item.value !== value));
  };

  // Create a custom save function with direct API call
  const handleSaveClick = async (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get form values directly
      const formValues = getValues() as IEvent;

      // Create a new object for the API call (not bound by IEvent type)
      const apiData: Record<string, any> = {
        ...formValues,
      };

      // Ensure dates are properly formatted
      if (formValues.startDate instanceof Date) {
        apiData.startDate = formValues.startDate.toISOString();
      } else if (typeof formValues.startDate === "string") {
        try {
          apiData.startDate = parseISO(formValues.startDate).toISOString();
        } catch (error) {
          console.error("Error parsing startDate:", error);
        }
      }

      if (formValues.endDate instanceof Date) {
        apiData.endDate = formValues.endDate.toISOString();
      } else if (typeof formValues.endDate === "string") {
        try {
          apiData.endDate = parseISO(formValues.endDate).toISOString();
        } catch (error) {
          console.error("Error parsing endDate:", error);
        }
      }

      // Create a minimal update object without security contacts
      const eventUpdateData = {
        name: apiData.name,
        description: apiData.description,
        startDate: apiData.startDate,
        endDate: apiData.endDate,
        latitude: apiData.latitude,
        longitude: apiData.longitude,
        geofenceRadius: apiData.geofenceRadius,
        isActive: apiData.isActive
      };

      // Get security contact IDs separately
      const securityContactIds = securityList
        .filter((item) => item.exists === true) // Only include existing users
        .map((item) => {
          // Use the ID directly from the item if available
          if (item.id) {
            return item.id;
          }

          // Fallback to search results if needed
          const matchedUser = searchResults.find(
            (user) =>
              (user.email && user.email === item.value) ||
              (user.phone && user.phone === item.value)
          );

          if (matchedUser?.id) {
            return matchedUser.id;
          }

          return null;
        })
        .filter((id): id is number => id !== null && id !== undefined); // Type guard to ensure only numbers remain

      // Combine event data with security contact IDs for a single update call
      const completeUpdateData = {
        ...eventUpdateData,
        securityContactIds: securityContactIds
      };

      // Make a single API call with all data
      const updateResponse = await axiosInstance.put(
        `${baseApiUrl}/safe-events/${params?.id}`,
        completeUpdateData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Redirect to list page on success
      window.location.href = "/safe-events";
    } catch (error) {
      console.error("Error updating event:", error);

      // Try to get more detailed error information if available
      if (axios.isAxiosError(error) && error.response) {
        console.error("Server error details:", {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }

      // Show error to user
      setInputError(`Error updating event: ${error instanceof Error ? error.message : "Server error"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Edit
      isLoading={formLoading || isSubmitting}
      saveButtonProps={{
        onClick: handleSaveClick,
        disabled: isSubmitting,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Controller
            control={control}
            name="isActive"
            defaultValue={EventData?.isActive ?? true}
            render={({ field }) => (
              <FormControlLabel
                label={translate("isActive.title", "Active")}
                control={
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                    }}
                    color="primary"
                  />
                }
              />
            )}
          />
        </Box>
        <Grid container spacing={2}>
          {/* First column with event details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("name", {
                    required: "This field is required",
                  })}
                  error={!!(errors as any)?.name}
                  helperText={(errors as any)?.name?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label={translate("events.fields.name", "Event Name")}
                  name="name"
                  defaultValue={EventData?.name}
                />

                <TextField
                  {...register("description")}
                  error={!!(errors as any)?.description}
                  helperText={(errors as any)?.description?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label={translate("events.fields.description", "Description")}
                  name="description"
                  multiline
                  rows={3}
                  defaultValue={EventData?.description}
                />

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    <Controller
                      control={control}
                      name="startDate"
                      // Remove the defaultValue here as we're setting it in useEffect
                      rules={{ required: "This field is required" }}
                      render={({ field }) => {
                        // Ensure field.value is a Date object
                        const dateValue =
                          field.value instanceof Date
                            ? field.value
                            : typeof field.value === "string"
                            ? parseISO(field.value)
                            : new Date();

                        return (
                          <DateTimePicker
                            label={translate(
                              "events.fields.startDate",
                              "Start Date"
                            )}
                            value={dateValue}
                            onChange={(date) => {
                              // Ensure we only pass Date objects
                              field.onChange(date instanceof Date ? date : null);
                            }}
                            slotProps={{
                              textField: {
                                margin: "normal",
                                fullWidth: true,
                                error: !!(errors as any)?.startDate,
                                helperText: (errors as any)?.startDate?.message,
                                InputLabelProps: { shrink: true },
                              },
                            }}
                          />
                        );
                      }}
                    />

                    <Controller
                      control={control}
                      name="endDate"
                      defaultValue={EventData?.endDate ? new Date(EventData?.endDate) : undefined}
                      render={({ field }) => {
                        return (
                          <DateTimePicker
                            {...field}
                            label={translate("events.fields.endDate", "End Date")}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                margin: "normal",
                                error: !!(errors as any)?.endDate,
                                helperText: (errors as any)?.endDate?.message,
                              },
                            }}
                            minDateTime={new Date()}
                          />
                        );
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </CardContent>
            </Card>
          </Grid>

          {/* Second column with date/time and location */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-end' }}>
                  <Box sx={{ flex: 1 }}>
                    <AddressAutocomplete
                      label={translate("events.fields.address", "Address")}
                      placeholder="Search for an address"
                      onAddressSelect={(result: GeoapifyResult) => {
                        // Update latitude and longitude values when an address is selected
                        setValue("latitude", result.lat);
                        setValue("longitude", result.lon);

                        // Force re-render of the map by updating the state
                        setMapCoordinates({
                          latitude: result.lat,
                          longitude: result.lon
                        });
                      }}
                    />
                  </Box>
                  <Box sx={{ width: '100px' }}>
                    <TextField
                      {...register("geofenceRadius", {
                        required: "This field is required",
                        valueAsNumber: true,
                      })}
                      error={!!(errors as any)?.geofenceRadius}
                      helperText={(errors as any)?.geofenceRadius?.message}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      type="number"
                      label={translate(
                        "events.fields.radius",
                        "Radius"
                      )}
                      name="geofenceRadius"
                      sx={{ marginBottom: 0 }}
                      defaultValue={EventData?.geofenceRadius}
                      onChange={(e) => {
                        register("geofenceRadius").onChange(e);
                        // Force a re-render of the map with the new radius
                        setTimeout(() => {
                          setMapCoordinates({...mapCoordinates});
                        }, 10);
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: 2, height: "400px", borderRadius: '6px' }}>
                  <Box sx={{ height: "100%", borderRadius: '6px', overflow: 'hidden' }}>
                    <LocationMap
                      latitude={mapCoordinates.latitude || getValues("latitude") || EventData?.latitude || 43.2862717}
                      longitude={mapCoordinates.longitude || getValues("longitude") || EventData?.longitude || 5.3832961}
                      radius={parseInt(getValues("geofenceRadius") || EventData?.geofenceRadius?.toString() || "100")}
                      onLocationChange={(lat, lng) => {
                        setValue("latitude", lat);
                        setValue("longitude", lng);

                        // Update the map coordinates state
                        setMapCoordinates({
                          latitude: lat,
                          longitude: lng
                        });
                      }}
                      showMarker={false}
                    />
                  </Box>
                </Box>

                {/* Hidden inputs for latitude and longitude that still register with the form */}
                <input
                  type="hidden"
                  {...register("latitude", {
                    required: "This field is required",
                    valueAsNumber: true,
                  })}
                  defaultValue={EventData?.latitude}
                />
                <input
                  type="hidden"
                  {...register("longitude", {
                    required: "This field is required",
                    valueAsNumber: true,
                  })}
                  defaultValue={EventData?.longitude}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Security Access Management */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add Security Access
                </Typography>
                <UserSearch
                  onUserSelect={handleAddUserFromSearch}
                  onManualAdd={handleAddSecurity}
                  apiUrl={apiUrl}
                  inputLabel="Search for security personnel"
                  showManualAdd={true}
                  addedContacts={{
                    emails: securityList
                      .filter((item) => item.email)
                      .map((item) => item.email as string),
                    phones: securityList
                      .filter((item) => item.phone)
                      .map((item) => item.phone as string),
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Security Access List
                </Typography>
                {securityList.length > 0 ? (
                  <List dense>
                    {securityList.map((item, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveSecurity(item.value)}
                          >
                            Remove
                          </Button>
                        }
                      >
                        <ListItemIcon>
                          {item.exists === null ? (
                            <span>⏳</span>
                          ) : item.exists ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <CancelIcon color="error" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name ? item.name : item.value}
                          secondary={
                            item.email && item.phone
                              ? `${item.email}\n・${item.phone}`
                              : item.email
                              ? `${item.email}`
                              : item.phone
                              ? `${item.phone}`
                              : item.value
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    align="center"
                    sx={{ py: 3 }}
                  >
                    No security personnel added yet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Edit>
  );
};
